import { Suspense, lazy, useEffect, useRef, useState, type ComponentType } from 'react'
import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom'

import { useQueryClient } from '@tanstack/react-query'

import { BottomNav } from './components/BottomNav'
import { BottomNavDirector } from './components/director/BottomNavDirector'
import { RouteBoundary } from './components/RouteErrorBoundary'
import { reloadOnceForStaleChunk } from './lazyReload'
import { useAuth, useActiveRole } from './auth/AuthProvider'
import { useLang } from './i18n/lang'
import { getJwt } from './api/client'
import { getUploadState, subscribeUpload, type UploadState } from './api/calls'
import { realtime, type RealtimeEvent } from './realtime/ws'
import { bumpPending } from './realtime/pending'
import { drainQueue, getQueueSize, subscribeQueue } from './offline'
import { ScreenSkeleton, t, useToast } from './design'
import { getStartParam, hapticImpact, setBackButton } from './telegram/tg'

// ── eager: the boot + unauthenticated path (rendered before the router, so
//    they belong in the initial chunk anyway). Everything else is lazy below. ──
import { OrphanScreen } from './screens/OrphanScreen'
import { SplashScreen } from './screens/SplashScreen'
import { TenantPickerScreen } from './screens/TenantPickerScreen'
import { AuthErrorScreen } from './screens/AuthErrorScreen'
import {
  AlreadyConnectedScreen,
  InviteExpiredScreen,
  InviteInvalidScreen,
} from './screens/onboarding/InviteOutcomeScreen'

// ── code-splitting (docs/miniapp-m7-tz.md §3.4) ──────────────────────────────
// In-router screens load on navigation; `manualChunks` (vite.config) groups all
// of `screens/director/*` into one "director" chunk a manager never downloads.
// Screens are named exports, so adapt each to lazy()'s default-export contract.
function lazyNamed<M extends Record<string, ComponentType<any>>>(
  loader: () => Promise<M>,
  name: keyof M,
) {
  return lazy(() =>
    loader()
      .then((m) => ({ default: m[name] }))
      .catch((err: unknown) => {
        // A failed chunk import after a deploy = stale hashes (Cloudflare serves
        // index.html for the gone /assets/*.js, so the module load rejects).
        // Reload once to pull the fresh build; if we already did, surface to the
        // RouteErrorBoundary instead of looping.
        if (reloadOnceForStaleChunk()) return new Promise<never>(() => {})
        throw err
      }),
  )
}

const OnboardingFlow = lazyNamed(() => import('./screens/onboarding/OnboardingFlow'), 'OnboardingFlow')
const TutorialCarousel = lazyNamed(() => import('./screens/onboarding/TutorialCarousel'), 'TutorialCarousel')

const DashboardScreen = lazyNamed(() => import('./screens/director/DashboardScreen'), 'DashboardScreen')
const TeamScreen = lazyNamed(() => import('./screens/director/TeamScreen'), 'TeamScreen')
const ManagerDetailScreen = lazyNamed(() => import('./screens/director/ManagerDetailScreen'), 'ManagerDetailScreen')
const CreateTaskScreen = lazyNamed(() => import('./screens/director/CreateTaskScreen'), 'CreateTaskScreen')
const AIReportsScreen = lazyNamed(() => import('./screens/director/AIReportsScreen'), 'AIReportsScreen')
const AIReportDetailScreen = lazyNamed(() => import('./screens/director/AIReportDetailScreen'), 'AIReportDetailScreen')
const ReportsArchiveScreen = lazyNamed(() => import('./screens/director/ReportsArchiveScreen'), 'ReportsArchiveScreen')
const ReportDetailScreen = lazyNamed(() => import('./screens/director/ReportDetailScreen'), 'ReportDetailScreen')
const WorkHoursScreen = lazyNamed(() => import('./screens/settings/WorkHoursScreen'), 'WorkHoursScreen')
const AiSettingsScreen = lazyNamed(() => import('./screens/director/AiSettingsScreen'), 'AiSettingsScreen')
const RecommendationsScreen = lazyNamed(() => import('./screens/director/RecommendationsScreen'), 'RecommendationsScreen')
const PipelinesScreen = lazyNamed(() => import('./screens/director/PipelinesScreen'), 'PipelinesScreen')

const TodayScreen = lazyNamed(() => import('./screens/TodayScreen'), 'TodayScreen')
const TaskScreen = lazyNamed(() => import('./screens/TaskScreen'), 'TaskScreen')
const AskAiScreen = lazyNamed(() => import('./screens/AskAiScreen'), 'AskAiScreen')
const DealsScreen = lazyNamed(() => import('./screens/DealsScreen'), 'DealsScreen')
const DealScreen = lazyNamed(() => import('./screens/DealScreen'), 'DealScreen')
const OverdueScreen = lazyNamed(() => import('./screens/OverdueScreen'), 'OverdueScreen')
const AIOverviewScreen = lazyNamed(() => import('./screens/AIOverviewScreen'), 'AIOverviewScreen')
const AIManagerScreen = lazyNamed(() => import('./screens/AIManagerScreen'), 'AIManagerScreen')
const CallsScreen = lazyNamed(() => import('./screens/CallsScreen'), 'CallsScreen')
const CallUploadScreen = lazyNamed(() => import('./screens/CallUploadScreen'), 'CallUploadScreen')
const CallDetailScreen = lazyNamed(() => import('./screens/CallDetailScreen'), 'CallDetailScreen')
const ReactivationScreen = lazyNamed(() => import('./screens/ReactivationScreen'), 'ReactivationScreen')
const ReactivationDraftScreen = lazyNamed(() => import('./screens/ReactivationDraftScreen'), 'ReactivationDraftScreen')
const SearchScreen = lazyNamed(() => import('./screens/SearchScreen'), 'SearchScreen')
const NotificationSettingsScreen = lazyNamed(() => import('./screens/NotificationSettingsScreen'), 'NotificationSettingsScreen')
const ProfileScreen = lazyNamed(() => import('./screens/ProfileScreen'), 'ProfileScreen')

export function App() {
  const { status } = useAuth()

  // Dev-only screen previews (?screen=splash|welcome|tutorial) for design work.
  if (import.meta.env.DEV) {
    const screen = new URLSearchParams(window.location.search).get('screen')
    if (screen === 'splash') return <div className="pt-app pt-shell"><SplashScreen /></div>
    if (screen === 'tutorial') {
      return <div className="pt-app pt-shell"><Suspense fallback={<SplashScreen />}><TutorialCarousel onDone={() => {}} /></Suspense></div>
    }
    // Preview a loading skeleton (?screen=skeleton&kind=deals|calls|list|detail|today).
    if (screen === 'skeleton') {
      const kind = new URLSearchParams(window.location.search).get('kind') || 'today'
      return <div className="pt-app pt-shell"><ScreenSkeleton kind={kind} /></div>
    }
    // Preview the per-screen crash recovery (RouteErrorBoundary fallback).
    if (screen === 'crash') {
      return (
        <div className="pt-app pt-shell">
          <HashRouter><RouteBoundary><CrashProbe /></RouteBoundary></HashRouter>
        </div>
      )
    }
  }

  // The whole SPA lives inside a single .pt-app shell: it carries the design
  // system's base reset/font and lays the screen + bottom nav out as a fixed
  // -height flex column (the screens use flex:1 + .scroll-y inside it).
  return <div className="pt-app pt-shell">{renderBody(status)}</div>
}

function renderBody(status: ReturnType<typeof useAuth>['status']) {
  if (status === 'idle' || status === 'authing') return <SplashScreen />
  if (status === 'orphan') return <OrphanScreen />
  if (status === 'picker') return <TenantPickerScreen />
  if (status === 'invite_expired') return <InviteExpiredScreen />
  if (status === 'invite_invalid') return <InviteInvalidScreen />
  if (status === 'already_connected') return <AlreadyConnectedScreen />
  if (status === 'error') return <AuthErrorScreen />

  return <PostAuth />
}

/** Dev-only probe (?screen=crash): throws on render so RouteBoundary shows its
 *  recovery fallback. Dead in production (guarded by import.meta.env.DEV). */
function CrashProbe(): never {
  throw new Error('CrashProbe: simulated render error for ErrorBoundary preview')
}

/** Gate the authed app behind the one-time onboarding when this session was
 *  created via a first-time invite acceptance (firstRun). */
function PostAuth() {
  const { firstRun } = useAuth()
  const [onboarded, setOnboarded] = useState(!firstRun)
  if (!onboarded) {
    return (
      <Suspense fallback={<SplashScreen />}>
        <OnboardingFlow onDone={() => setOnboarded(true)} />
      </Suspense>
    )
  }
  return <AuthedApp />
}

/** Replay the tutorial from Profile (tut.replay). Done → back to where you were. */
function TutorialReplay() {
  const navigate = useNavigate()
  return (
    <Suspense fallback={<SplashScreen />}>
      <TutorialCarousel onDone={() => navigate(-1)} />
    </Suspense>
  )
}

/** The authed app shell. Picks the manager or director routes + nav from the
 *  active-tenant role (docs/miniapp-m3-tz.md §3.2). The cross-cutting helpers
 *  (back button, deeplinks, realtime, offline) are shared by both. */
function AuthedApp() {
  const role = useActiveRole()
  // Subscribe to the language so a runtime switch re-renders the WHOLE shell —
  // screens AND persistent chrome (BottomNav, headers). The ported t() reads
  // window.__lang at render time and isn't reactive on its own, so without this
  // the nav/header labels would lag behind the chosen language. Re-render, not
  // remount: HashRouter keeps the route and RealtimeBridge keeps its socket.
  const { lang } = useLang()
  return (
    <HashRouter>
      <BackButtonSync />
      <DeeplinkSync />
      <RealtimeBridge />
      <OfflineBar />
      <CallUploadBar />
      <div data-lang={lang} style={{ display: 'contents' }}>
        {role === 'director' ? <DirectorShell /> : <ManagerShell />}
      </div>
    </HashRouter>
  )
}

/** Page transitions (M-7 §3.8): the routed screen slides in from the right on a
 *  forward navigate and from the left on a Back (POP). A subtle 18px slide+fade,
 *  not a full-width swipe; the transform clears when the animation ends (no
 *  fill-mode) so it never becomes a containing block for sheet/sticky children.
 *  Re-keys on pathname so it re-animates each navigation. */
function RouteTransition({ children }: { children: React.ReactNode }) {
  const navType = useNavigationType()
  const { pathname } = useLocation()
  return (
    <div key={pathname} className={`route-screen ${navType === 'POP' ? 'route-back' : 'route-fwd'}`}>
      {children}
    </div>
  )
}

function ManagerShell() {
  return (
    <>
      <RouteBoundary>
      <RouteTransition>
      <Suspense fallback={<ScreenSkeleton />}>
      <Routes>
        <Route path="/today" element={<TodayScreen />} />
        <Route path="/task/:id" element={<TaskScreen />} />
        <Route path="/ask-ai/:taskId" element={<AskAiScreen />} />
        <Route path="/deals" element={<DealsScreen />} />
        <Route path="/deals/:id" element={<DealScreen />} />
        <Route path="/deals/:id/:tab" element={<DealScreen />} />
        <Route path="/overdue" element={<OverdueScreen />} />
        <Route path="/ai" element={<AIManagerScreen />} />
        <Route path="/calls" element={<CallsScreen />} />
        <Route path="/calls/upload" element={<CallUploadScreen />} />
        <Route path="/calls/:id" element={<CallDetailScreen />} />
        <Route path="/reactivation" element={<ReactivationScreen />} />
        <Route path="/reactivation/:dealId" element={<ReactivationDraftScreen />} />
        <Route path="/search" element={<SearchScreen />} />
        <Route path="/notifications" element={<NotificationSettingsScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/tutorial" element={<TutorialReplay />} />
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Routes>
      </Suspense>
      </RouteTransition>
      </RouteBoundary>
      <BottomNavGate />
    </>
  )
}

function DirectorShell() {
  return (
    <>
      <RouteBoundary>
      <RouteTransition>
      <Suspense fallback={<ScreenSkeleton />}>
      <Routes>
        <Route path="/dashboard" element={<DashboardScreen />} />
        <Route path="/team" element={<TeamScreen />} />
        <Route path="/team/:manager_id" element={<ManagerDetailScreen />} />
        <Route path="/create-task" element={<CreateTaskScreen />} />
        <Route path="/ai" element={<AIOverviewScreen />} />
        <Route path="/ai-reports" element={<AIReportsScreen />} />
        <Route path="/ai-reports/:id" element={<AIReportDetailScreen />} />
        <Route path="/reports" element={<ReportsArchiveScreen />} />
        <Route path="/reports/:id" element={<ReportDetailScreen />} />
        <Route path="/calls" element={<CallsScreen />} />
        <Route path="/calls/upload" element={<CallUploadScreen />} />
        <Route path="/calls/:id" element={<CallDetailScreen />} />
        <Route path="/reactivation" element={<ReactivationScreen />} />
        <Route path="/reactivation/:dealId" element={<ReactivationDraftScreen />} />
        <Route path="/search" element={<SearchScreen />} />
        <Route path="/notifications" element={<NotificationSettingsScreen />} />
        <Route path="/settings/work-hours" element={<WorkHoursScreen />} />
        <Route path="/ai-settings" element={<AiSettingsScreen />} />
        <Route path="/recommendations" element={<RecommendationsScreen />} />
        <Route path="/pipelines" element={<PipelinesScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/tutorial" element={<TutorialReplay />} />
        {/* Director can drill into deals/tasks from the dashboard + deeplinks. */}
        <Route path="/task/:id" element={<TaskScreen />} />
        <Route path="/ask-ai/:taskId" element={<AskAiScreen />} />
        <Route path="/deals" element={<DealsScreen />} />
        <Route path="/deals/:id" element={<DealScreen />} />
        <Route path="/deals/:id/:tab" element={<DealScreen />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
      </RouteTransition>
      </RouteBoundary>
      <DirectorNavGate />
    </>
  )
}

/** BottomNav is visible on root screens only. Detail screens hide it
 *  to give actions bars / tab bars the full vertical real estate. */
function BottomNavGate() {
  const location = useLocation()
  const path = location.pathname
  const showNav =
    path === '/' ||
    path === '/today' ||
    path === '/deals' ||
    path === '/overdue' ||
    path === '/ai' ||
    path === '/calls' ||
    path === '/profile'
  if (!showNav) return null
  return <BottomNav />
}

/** Director bottom nav on root tabs only; hidden on drill-in (task/deal). */
function DirectorNavGate() {
  const path = useLocation().pathname
  // /create-task is a focused form (sticky CTA + back button) → no bottom nav.
  const roots = ['/', '/dashboard', '/team', '/deals', '/ai', '/ai-reports', '/calls', '/profile']
  if (!roots.includes(path)) return null
  return <BottomNavDirector />
}

/** Offline status bar + queue indicator (docs/miniapp-m2-tz.md §3.8).
 *  Hidden while online with an empty queue; shows the "no connection" warning
 *  offline, and a "syncing N" strip while a backed-up queue drains. Toasts once
 *  when an action is parked. */
function OfflineBar() {
  const toast = useToast()
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine)
  const [queued, setQueued] = useState(getQueueSize())

  useEffect(() => {
    const onOnline = () => { setOnline(true); void drainQueue() }
    const onOffline = () => setOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    const unsub = subscribeQueue((size, justQueued) => {
      setQueued(size)
      if (justQueued) toast(t('rt.queuedToast'))
    })
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      unsub()
    }
  }, [toast])

  if (online && queued === 0) return null

  const isWarning = !online
  return (
    <div
      style={{
        flexShrink: 0,
        padding: '8px 16px',
        textAlign: 'center',
        font: '600 13px/1.3 var(--font-sans)',
        background: isWarning ? 'var(--warning-bg)' : 'var(--ai-bg)',
        color: isWarning ? 'var(--warning)' : 'var(--ai)',
      }}
    >
      {online
        ? `${t('rt.syncing')}${queued ? ` · ${t('rt.queued', { n: queued })}` : ''}`
        : `${t('rt.offline')}${queued ? ` · ${t('rt.queued', { n: queued })}` : ''}`}
    </div>
  )
}

/** Session-level call-upload progress strip (docs/miniapp-m6-tz.md §3.9). An
 *  upload runs in api/calls.ts's singleton so it survives navigation; this bar
 *  surfaces it everywhere except the upload screen (which shows its own UI).
 *  Tap → jump to the analyzed report when it's ready. */
function CallUploadBar() {
  const navigate = useNavigate()
  const path = useLocation().pathname
  const [state, setState] = useState<UploadState | null>(getUploadState())
  useEffect(() => subscribeUpload(setState), [])

  if (path === '/calls/upload' || !state) return null
  if (state.phase !== 'uploading' && state.phase !== 'confirming' && state.phase !== 'analyzing') return null

  const analyzing = state.phase === 'analyzing'
  const pct = Math.round(state.progress * 100)
  return (
    <button
      onClick={() => { if (analyzing && state.callId) navigate(`/calls/${encodeURIComponent(state.callId)}`) }}
      style={{
        flexShrink: 0, width: '100%', border: 'none', cursor: analyzing ? 'pointer' : 'default',
        padding: '8px 16px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8,
        font: '600 13px/1.3 var(--font-sans)', background: 'var(--ai-bg)', color: 'var(--ai)',
      }}
    >
      <span>📤</span>
      <span style={{ flex: 1 }}>{analyzing ? t('cl.bar.analyzing') : t('cl.bar.uploading', { p: pct })}</span>
      {!analyzing && (
        <span style={{ width: 64, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: 'var(--ai)' }} />
        </span>
      )}
    </button>
  )
}

/** Live stream bridge (docs/miniapp-m2-tz.md §3.9). Mounted only inside the
 *  authed tree, so it connects once the JWT exists. Server events invalidate
 *  the relevant React Query caches → the screens refetch without a manual
 *  pull-to-refresh. */
function RealtimeBridge() {
  const qc = useQueryClient()
  useEffect(() => {
    const jwt = getJwt()
    if (!jwt) return

    const off = realtime.on((e: RealtimeEvent) => {
      switch (e.type) {
        case 'task.created':
          // Don't refetch Today out from under the manager's finger — surface a
          // "Показать новые" pill (Состояния интерфейса · live) instead; tapping
          // it reveals the new task(s). Overdue can refresh silently.
          hapticImpact('medium')
          bumpPending(1)
          qc.invalidateQueries({ queryKey: ['overdue'] })
          break
        case 'task.completed':
        case 'task.updated':
        case 'task.escalated':
          qc.invalidateQueries({ queryKey: ['today'] })
          qc.invalidateQueries({ queryKey: ['overdue'] })
          if (typeof e.task_id === 'number') {
            qc.invalidateQueries({ queryKey: ['task', e.task_id] })
          }
          break
        case 'deal.stage_changed':
          if (typeof e.deal_id === 'number') {
            qc.invalidateQueries({ queryKey: ['deal', e.deal_id] })
          }
          qc.invalidateQueries({ queryKey: ['deals'] })
          break
        case 'team.load_changed':
          // Director-only: a manager's load shifted → refresh the overview.
          qc.invalidateQueries({ queryKey: ['dashboard'] })
          qc.invalidateQueries({ queryKey: ['team'] })
          break
      }
    })

    realtime.connect(jwt)
    return () => {
      off()
      realtime.close()
    }
  }, [qc])

  return null
}

/** Telegram deeplink: ?startapp=task_<id> / deal_<id> opens the app straight
 *  on that screen (docs/miniapp-m2-tz.md §3.10). Runs once on mount — after
 *  that the hash router owns navigation. */
function DeeplinkSync() {
  const navigate = useNavigate()
  const done = useRef(false)
  useEffect(() => {
    if (done.current) return
    done.current = true
    const sp = getStartParam()
    if (!sp) return
    const m = /^(task|deal)_(\d+)$/.exec(sp)
    if (!m) return
    const [, kind, id] = m
    navigate(kind === 'task' ? `/task/${id}` : `/deals/${id}`, { replace: true })
  }, [navigate])
  return null
}

/** Drives the native Telegram BackButton from the current route.
 *  Detail screens (/task/:id, /deals/:id) show it; root tabs hide it. */
function BackButtonSync() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const path = location.pathname
    const isDetail =
      /^\/task\//.test(path) || /^\/deals\/\d+/.test(path) ||
      /^\/team\/\d+/.test(path) || /^\/create-task/.test(path) ||
      /^\/ai-reports\/.+/.test(path) || /^\/tutorial/.test(path) ||
      /^\/ask-ai\//.test(path) || /^\/calls\/.+/.test(path) ||
      /^\/reactivation/.test(path) || /^\/reports/.test(path) ||
      /^\/search/.test(path) || /^\/notifications/.test(path) ||
      /^\/settings\//.test(path) || /^\/ai-settings/.test(path) ||
      /^\/pipelines/.test(path)
    if (isDetail) {
      return setBackButton(true, () => navigate(-1))
    }
    return setBackButton(false)
  }, [location.pathname, navigate])

  return null
}
